/**
 * Email validation regex
 * Validates email addresses according to RFC 5322 standard
 */
export const EMAIL_REGEX =
  /^(?=.{1,254}$)(?=.{1,64}@)[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:(?!-)[A-Za-z0-9-]{1,63}(?<!-)\.)+[A-Za-z]{2,63}$/;

/**
 * Name validation regex (letters, spaces, apostrophes, hyphens)
 */
export const NAME_REGEX = /^[\p{L}' -]+$/u;

/**
 * Regex to check last character is alpha
 */
export const ENDS_WITH_ALPHA = /\p{L}$/u;

/**
 * Regex to check if string ends with common punctuation (.!?;:)
 */
export const ENDS_WITH_PUNCTUATION = /[.!?;:]$/;

/**
 * number validation regex (numeric only)
 */
export const WHOLE_NUMBER_REGEX = /^(0|[1-9]\d*)$/;

export const TWO_DECIMAL_NUMBER_REGEX = /^(0|[1-9]\d*)(\.\d{1,2})?$/;

/**
 * Validates that a value contains only numeric characters.
 * @example
 * validate: { isNumber: validators.isNumber }
 */
export const isWholeNumber = (v: string) => {
  if (v === "" || v == null) {
    return true;
  }
  return WHOLE_NUMBER_REGEX.test(v.trim());
};

/**
 * Validates that a value contains numeric characters, up to two decimal points
 * @example
 * validate: { isDecimalNumber: validators.isDecimalNumber }
 */
export const isDecimalNumber = (v: string) => {
  if (v === "" || v == null) {
    return true;
  }
  return TWO_DECIMAL_NUMBER_REGEX.test(v.trim());
};

/**
 * Strips any minus sign from a numeric input value. A `type="number"` field's
 * `min` attribute only constrains the spinner arrows, so this guards manual
 * entry/paste of negative values.
 * @example
 * onChange={e => update({ dose: stripNegative(e.target.value) })}
 */
export const stripNegative = (v: string): string => v.replace(/-/g, "");

/**
 * Creates a validator that checks if value meets minimum threshold.
 * @param threshold - Minimum allowed value
 * @param errorMessage - Error message
 * @example
 * validate: {
 *   minimum: minValidator(30, "pages.addPatient.goals.valid.pulse.minimum")
 * }
 */

export const minValidator =
  (threshold: number, errorMessage: string) =>
  (value: string): boolean | string => {
    if (!value) {
      return true;
    }
    const n = toNum(value);
    if (n === null) {
      return true;
    }
    return n >= threshold || errorMessage;
  };
/**
 * Creates a validator that checks if value doesn't exceed maximum threshold.
 * @param threshold - Maximum allowed value
 * @param errorMessage - Error message
 * @example
 * validate: {
 *   maximum: maxValidator(220, "pages.addPatient.goals.valid.pulse.maximum")
 * }
 */
export const maxValidator =
  (threshold: number, errorMessage: string) =>
  (value: string): boolean | string => {
    if (!value) {
      return true;
    }
    const n = toNum(value);
    if (n === null) {
      return true;
    }
    return n <= threshold || errorMessage;
  };

/**
 * Creates a validator that checks if current value is smaller than target field value.
 * @param targetField - Value of field to compare against
 * @param errorMessage - Error message
 * @example
 * validate: {
 *   minSmaller: smallerThanValidator(
 *     getValues("weightMax"),
 *     translate("pages.addPatient.goals.valid.minMustBeSmaller"),
 *   )
 * }
 */
export const smallerThanValidator =
  (targetField: string, errorMessage: string) =>
  (value: string): boolean | string => {
    if (!value || !targetField) {
      return true;
    }
    const a = toNum(value);
    const b = toNum(targetField);
    if (a === null || b === null) {
      return true;
    }
    return a < b || errorMessage;
  };

/**
 * Creates a validator that checks if current value is larger than target field value.
 * @param targetField - Name of field to compare against
 * @param errorMessage - Error message
 * @example
 * validate: {
 *   maxLarger: largerThanValidator(
 *     getValues("weightMin"),
 *     "translate(pages.addPatient.goals.valid.maxMustBeLarger)",
 *   )
 * }
 */
export const largerThanValidator =
  (targetField: string, errorMessage: string) =>
  (value: string): boolean | string => {
    if (!value || !targetField) {
      return true;
    }
    const a = toNum(value);
    const b = toNum(targetField);
    if (a === null || b === null) {
      return true;
    }
    return a > b || errorMessage;
  };

const toNum = (s: string) => {
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};
